/**
 * Text utilities for inline editing
 */

/**
 * Extracts plain text from HTML content
 * @param {string} html - HTML string
 * @returns {string} - Plain text without HTML tags
 */
export const extractPlainTextFromHTML = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  // Create a temporary div to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Get text content (automatically strips tags)
  const text = temp.textContent || temp.innerText || '';
  
  return text;
};

/**
 * Determines which field to edit for inline editing based on element type
 * @param {string} type - Element type (e.g., 'title', 'text')
 * @returns {string|null} - Field name to edit, or null if not supported
 */
export const getInlineEditableField = (type) => {
  const fieldMap = {
    'title': 'text',
  };
  
  return fieldMap[type] || null;
};

/**
 * Gets the display value for inline editing (handles HTML vs plain text)
 * @param {object} element - The element being edited
 * @param {string} field - The field name
 * @returns {string} - The value to display in the editor
 */
export const getInlineEditValue = (element, field) => {
  if (!element) return '';
  
  // For title and other simple fields
  return element[field] || '';
};

/**
 * Checks if an element type should use HTML editing (preserves formatting)
 * @param {string} type - Element type
 * @returns {boolean} - True if type should edit as HTML
 */
export const shouldEditAsHTML = (type) => {
  // Only Text items should edit with HTML formatting preserved
  return type === 'text';
};

/**
 * Converts HTML to Markdown
 * Handles common formatting: bold, italic, links, paragraphs, line breaks
 * @param {string} html - HTML string
 * @returns {string} - Markdown string
 */
export const htmlToMarkdown = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  // Create a temporary div to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Process the HTML recursively
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }
    
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }
    
    const tagName = node.tagName.toLowerCase();
    const children = Array.from(node.childNodes).map(processNode).join('');
    
    // Convert HTML tags to markdown
    switch (tagName) {
      case 'strong':
      case 'b':
        return `**${children}**`;
      case 'em':
      case 'i':
        return `*${children}*`;
      case 'a':
        const href = node.getAttribute('href') || '';
        return `[${children}](${href})`;
      case 'p':
        return `${children}\n\n`;
      case 'br':
        return '\n';
      case 'h1':
        return `# ${children}\n\n`;
      case 'h2':
        return `## ${children}\n\n`;
      case 'h3':
        return `### ${children}\n\n`;
      case 'h4':
        return `#### ${children}\n\n`;
      case 'h5':
        return `##### ${children}\n\n`;
      case 'h6':
        return `###### ${children}\n\n`;
      case 'ul':
        return `${children}\n`;
      case 'ol':
        return `${children}\n`;
      case 'li':
        return `- ${children}\n`;
      case 'code':
        return `\`${children}\``;
      case 'pre':
        return `\`\`\`\n${children}\n\`\`\`\n`;
      case 'blockquote':
        return `> ${children}\n`;
      case 'hr':
        return '---\n\n';
      case 'div':
      case 'span':
        return children;
      default:
        return children;
    }
  };
  
  const markdown = Array.from(temp.childNodes)
    .map(processNode)
    .join('')
    .trim();
  
  // Clean up extra newlines
  return markdown.replace(/\n{3,}/g, '\n\n');
};

/**
 * Checks if an element type supports inline editing
 * @param {string} type - Element type
 * @returns {boolean} - True if type supports inline editing
 */
export const supportsInlineEditing = (type) => {
  return type === 'title';
};

